import { useCallback, useState } from "react";

/**
 * Column model and visibility preferences shared by every spreadsheet in the
 * app (#911, epic #872).
 *
 * Extracted from ListSheet so Food and Shopping Lists cannot drift apart on the
 * things a user notices immediately: which columns a phone shows first, whether
 * the picker remembers a choice, and whether the name column can be hidden.
 * Generic over the column-key type so each feature keeps its own domain columns
 * — the shared part is the BEHAVIOUR, not the vocabulary.
 */

export type SheetColumnDef<Key extends string> = {
  key: Key;
  /** English header text, and the fallback whenever `labelKey` is absent. */
  label: string;
  /**
   * Bundle key for the header, e.g. "sheet.column.price". Resolved against the
   * `common` namespace by the shared sheet chrome, so a feature whose columns
   * live in its own namespace spells it "<ns>:<key>". Optional: a column
   * without one keeps rendering `label`, which is what makes this additive for
   * the sheets that have not been translated yet.
   */
  labelKey?: string;
  /** CSS grid track for this column, e.g. "88px" or "minmax(160px, 2.4fr)". */
  width: string;
  /** Columns the user may not hide. There is always at least one: a sheet with
   * every column hidden is a blank rectangle with no way back. */
  always?: boolean;
  inputMode?: "decimal" | "numeric" | "url";
  /** `<datalist>` id backing the cell's suggestions. */
  listId?: string;
  /** Right-aligns the header and cell, for numeric columns. */
  numeric?: boolean;
};

/** One storage key per FEATURE, never one global key: the columns a shopper
 * wants on a grocery list and the columns they want on the food catalog are
 * different questions, and #911 requires the two preferences stay separate. */
export function sheetColumnStorageKey(storageKey: string): string {
  return `household.listSheet.columns.${storageKey}`;
}

/** Phones start narrow and the user opens up from there. 640px rather than the
 * 720px the old food grid used, so both sheets agree on where "phone" ends. */
export const SHEET_MOBILE_QUERY = "(max-width: 640px)";

/** Room for one `.icon-button` (42px wide). The default action track, and all
 * Shopping needs — it renders a single delete per row. */
export const SHEET_ACTION_COLUMN_WIDTH = "44px";

/** The action track when the sheet also carries the on-hand chip (#1058).
 *
 * Fixed rather than `auto` for the reason documented on `actionColumnWidth`: an
 * auto track sizes to each row's own content, so a row with stock and a row
 * without would stop lining up with each other and with the header. Widening
 * the track for the whole sheet is what keeps the grid honest — the chip lives
 * in the same cell as the delete button, and at 44px it simply pushed the
 * delete off the row (caught in the browser, not by a test). The chip
 * ellipsizes inside the extra room, since a food can legitimately be
 * "2 gal, 1 qt"; the full figure stays in its title and aria-label. */
export const SHEET_ACTION_COLUMN_WIDTH_WITH_STOCK = "132px";

function readStoredKeys<Key extends string>(storageKey: string, columns: SheetColumnDef<Key>[]): Key[] | null {
  try {
    const raw = window.localStorage.getItem(sheetColumnStorageKey(storageKey));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    // Filtered through the CURRENT column set, so a stored key that a later
    // release removed is dropped rather than rendering an empty track, and the
    // order is always the declared one rather than whatever was saved.
    const keys = columns.map((column) => column.key).filter((key) => parsed.includes(key));
    return withAlwaysColumns(keys, columns);
  } catch {
    return null;
  }
}

/** Locked columns are re-inserted in declared order whatever the input said, so
 * no stored preference and no toggle can hide them. */
function withAlwaysColumns<Key extends string>(keys: Key[], columns: SheetColumnDef<Key>[]): Key[] {
  const wanted = new Set(keys);
  return columns.filter((column) => column.always || wanted.has(column.key)).map((column) => column.key);
}

/**
 * Visible-column state with per-feature persistence.
 *
 * `mobileDefaults` applies only when nothing is stored: once the user has
 * chosen, their choice travels between phone and desktop rather than being
 * silently re-narrowed on the next visit from a phone.
 */
export function useSheetColumns<Key extends string>(input: {
  storageKey: string;
  columns: SheetColumnDef<Key>[];
  desktopDefaults: Key[];
  mobileDefaults: Key[];
  /**
   * Width of the trailing per-row action track. Defaults to one icon button,
   * which is what Shopping renders (delete).
   *
   * A sheet with more row actions MUST reserve the room. The track is a fixed
   * length rather than `auto` on purpose: every row is its own grid element, so
   * an `auto` track would size to each row's own buttons and rows would stop
   * lining up with the header the moment one row was dirty and its neighbour
   * was not.
   */
  actionColumnWidth?: string;
}): {
  visibleKeys: Key[];
  visibleColumns: SheetColumnDef<Key>[];
  gridTemplate: string;
  toggleColumn: (key: Key) => void;
} {
  const { actionColumnWidth = SHEET_ACTION_COLUMN_WIDTH, columns, desktopDefaults, mobileDefaults, storageKey } = input;
  const [visibleKeys, setVisibleKeys] = useState<Key[]>(() => {
    const stored = readStoredKeys(storageKey, columns);
    if (stored) return stored;
    // `matchMedia` is guarded rather than assumed: the web unit suite renders
    // these components outside a browser, and a sheet that throws on mount
    // would be untestable without a DOM shim.
    const narrow = typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(SHEET_MOBILE_QUERY).matches
      : false;
    return withAlwaysColumns(narrow ? mobileDefaults : desktopDefaults, columns);
  });

  const toggleColumn = useCallback((key: Key) => {
    setVisibleKeys((current) => {
      const next = current.includes(key) ? current.filter((existing) => existing !== key) : [...current, key];
      const resolved = withAlwaysColumns(next, columns);
      try {
        window.localStorage.setItem(sheetColumnStorageKey(storageKey), JSON.stringify(resolved));
      } catch {
        // A full or blocked localStorage must not break the sheet; the choice
        // simply does not survive the session.
      }
      return resolved;
    });
  }, [columns, storageKey]);

  const visibleColumns = columns.filter((column) => visibleKeys.includes(column.key));
  // The trailing track is the per-row action column every sheet carries.
  const gridTemplate = `${visibleColumns.map((column) => column.width).join(" ")} ${actionColumnWidth}`;

  return { visibleKeys, visibleColumns, gridTemplate, toggleColumn };
}
